import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '@mondy/shared/api-client';
import { useCurrentUser, useLogout } from '@/hooks/useCurrentUser';
import { useInvitePreview } from '@/hooks/useInvitePreview';
import { useAcceptInvite } from '@/hooks/useAcceptInvite';
import { useSetActiveGroup } from '@/state/activeGroup';
import { Spinner } from '@/components/Spinner';
import { JoinedGroupModal } from '@/components/JoinedGroupModal';

/**
 * `/accept_group_invite/:token` — the landing page that email invite
 * recipients hit. Three-state machine driven by `GET /api/invites/:token/preview`
 * (unauthenticated):
 *
 *   1. Logged-in AND email matches invite → show "Accept invitation" button.
 *   2. Logged-in AND email doesn't match → offer logout + sign-in-as-different.
 *   3. Logged-out → two buttons, "Log in" vs "Create account". Both preserve
 *      `?next=/accept_group_invite/:token` so the flow resumes post-auth.
 *      Signup additionally carries the `inviteToken` and `email` so the
 *      server pre-stores the invite and signup can pre-fill the address.
 */
export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const preview = useInvitePreview(token ?? null);
  const user = useCurrentUser();

  if (!token) {
    return <StaticMessage title="Invalid invitation" body="This link is missing its token." />;
  }

  if (preview.isLoading || user === undefined) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-6 py-8">
        <Spinner label="Loading invitation" />
      </main>
    );
  }

  if (preview.isError) {
    const err = preview.error;
    if (err instanceof ApiError && err.status === 404) {
      return (
        <StaticMessage
          title="Invitation not found"
          body="This invitation is no longer valid. Ask the group owner to send a new one."
        />
      );
    }
    if (err instanceof ApiError && err.status === 410) {
      return (
        <StaticMessage
          title="Invitation expired"
          body="This invitation has expired. Ask the group owner to send a new one."
        />
      );
    }
    return (
      <StaticMessage
        title="Something went wrong"
        body="Could not load this invitation. Try again in a moment."
      />
    );
  }

  const data = preview.data!.preview;

  if (!user) {
    return <LoggedOutView token={token} preview={data} />;
  }

  // Compare case-insensitively because email addresses are case-preserved
  // in storage but case-insensitive semantically.
  const emailMatches = user.email.toLowerCase() === data.email.toLowerCase();
  if (!emailMatches) {
    return <EmailMismatchView token={token} invitedEmail={data.email} currentEmail={user.email} />;
  }

  return <AcceptView token={token} preview={data} />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col px-6 py-8">
      <Link
        to="/"
        className="mb-10 flex items-center gap-2 text-sm text-[color:var(--color-fg-muted)]"
      >
        <span aria-hidden>←</span> Home
      </Link>
      <div className="flex flex-1 flex-col justify-center gap-6">{children}</div>
    </main>
  );
}

function StaticMessage({ title, body }: { title: string; body: string }) {
  return (
    <Shell>
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">{body}</p>
      </header>
      <Link
        to="/"
        className="h-11 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 text-center font-medium leading-[44px]"
      >
        Go home
      </Link>
    </Shell>
  );
}

function LoggedOutView({
  token,
  preview,
}: {
  token: string;
  preview: { groupName: string; ownerDisplayName: string; email: string };
}) {
  const next = `/accept_group_invite/${token}`;
  const signupParams = new URLSearchParams({
    next,
    inviteToken: token,
    email: preview.email,
  });
  return (
    <Shell>
      <header className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-[0.22em] text-[color:var(--color-fg-muted)]">
          Group invitation
        </span>
        <h1 className="text-2xl font-semibold">
          {preview.ownerDisplayName} invited you to join{' '}
          <span style={{ color: 'var(--hot)' }}>{preview.groupName}</span>.
        </h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          Sent to{' '}
          <b className="text-[color:var(--color-fg)] break-all">
            {preview.email}
          </b>
          .
        </p>
      </header>
      <div className="flex flex-col gap-3">
        <Link
          to={`/login?${new URLSearchParams({ next }).toString()}`}
          className="h-11 rounded-xl bg-[color:var(--color-accent)] px-4 text-center font-medium leading-[44px] text-[color:var(--color-accent-fg)]"
        >
          Log in
        </Link>
        <Link
          to={`/signup?${signupParams.toString()}`}
          className="h-11 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 text-center font-medium leading-[44px]"
        >
          Create an account
        </Link>
      </div>
    </Shell>
  );
}

function EmailMismatchView({
  token,
  invitedEmail,
  currentEmail,
}: {
  token: string;
  invitedEmail: string;
  currentEmail: string;
}) {
  const logout = useLogout();
  const navigate = useNavigate();

  function onSwitch() {
    logout();
    const next = `/accept_group_invite/${token}`;
    navigate(`/login?${new URLSearchParams({ next }).toString()}`);
  }

  return (
    <Shell>
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">Wrong account</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          This invitation was sent to{' '}
          <b className="break-all text-[color:var(--color-fg)]">{invitedEmail}</b>
          , but you're signed in as{' '}
          <b className="break-all text-[color:var(--color-fg)]">{currentEmail}</b>
          .
        </p>
      </header>
      <button
        type="button"
        onClick={onSwitch}
        className="min-h-11 rounded-xl bg-[color:var(--color-accent)] px-4 py-2 text-sm font-medium leading-snug text-[color:var(--color-accent-fg)] sm:text-base"
      >
        <span className="sm:hidden">Log out &amp; switch account</span>
        <span className="max-sm:hidden">
          Log out and sign in with a different account
        </span>
      </button>
    </Shell>
  );
}

function AcceptView({
  token,
  preview,
}: {
  token: string;
  preview: { groupName: string; ownerDisplayName: string };
}) {
  const accept = useAcceptInvite();
  const setActiveGroupId = useSetActiveGroup();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [joinedName, setJoinedName] = useState<string | null>(null);

  async function onAccept() {
    setError(null);
    try {
      const res = await accept.mutateAsync(token);
      setActiveGroupId(res.group.id);
      setJoinedName(res.group.name);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setError('This invitation is no longer valid.');
          return;
        }
        if (err.status === 410) {
          setError('This invitation has expired.');
          return;
        }
        if (err.code === 'EMAIL_MISMATCH') {
          setError(
            "This invitation was sent to a different email. Log out and sign in with that address.",
          );
          return;
        }
        setError(err.message);
        return;
      }
      setError('Could not accept invitation.');
    }
  }

  return (
    <>
      <Shell>
        <header className="flex flex-col gap-2">
          <span className="text-xs uppercase tracking-[0.22em] text-[color:var(--color-fg-muted)]">
            Group invitation
          </span>
          <h1 className="text-2xl font-semibold">
            Join <span style={{ color: 'var(--hot)' }}>{preview.groupName}</span>?
          </h1>
          <p className="text-sm text-[color:var(--color-fg-muted)]">
            Invited by {preview.ownerDisplayName}.
          </p>
        </header>
        <button
          type="button"
          onClick={onAccept}
          disabled={accept.isPending}
          className="h-11 rounded-xl bg-[color:var(--color-accent)] px-4 font-medium text-[color:var(--color-accent-fg)] disabled:pointer-events-none disabled:opacity-60"
        >
          {accept.isPending ? 'Accepting…' : 'Accept invitation'}
        </button>
        {error ? (
          <p className="text-sm text-[color:var(--color-danger)]">{error}</p>
        ) : null}
      </Shell>

      {joinedName ? (
        <JoinedGroupModal
          name={joinedName}
          onDismiss={() => {
            setJoinedName(null);
            navigate('/standings');
          }}
        />
      ) : null}
    </>
  );
}

